/**
 * `create_artifact` — the ONE executor for artifact creation.
 *
 * The model calls this tool with a substantial deliverable (document, code
 * file, HTML snippet, data table). The FULL content is emitted onto the UI
 * message stream as a `data-artifact` part via the injected emitter; only a
 * SMALL ack (`{ artifactId, title, kind, chars }`) returns into model context.
 * That asymmetry is the point: the deliverable renders in a dedicated card
 * without its body being re-paid as model tokens on every subsequent step.
 *
 * Executor parity (CHAT_TOOL_EXECUTOR_PARITY): every surface — the package's
 * own registry path AND hub's `getChatToolsForStream` registry — resolves to
 * `executeCreateArtifact` below. The tool needs NO approval: it is a pure
 * content emission with no side effects outside the chat stream.
 *
 * Graceful degrade (documented + tested): when no emitter is wired (a surface
 * without a UI message stream, e.g. Slack or MCP), the tool returns an
 * error-shaped result telling the model to present the content directly in its
 * reply. The model authored the content (it is the tool INPUT), so returning
 * it inline would only duplicate tokens — declining is strictly cheaper.
 */

import { tool } from 'ai';
import { z } from 'zod';
// Deep import, NOT the '@wtc/ui' barrel: this module runs server-side inside the
// tool registry, and the barrel would drag the whole React component graph into
// its import path. `chart-spec` is zod-only by design.
import { ChartSpecSchema } from '@wtc/ui/components/charts/chart-spec';
import type { JSONSchema7 } from 'json-schema';
import type { Tool as MeridianRegistryTool, ToolExecutionContext, ToolResult } from '../tools/types';
import {
  MODEL_CREATABLE_ARTIFACT_KINDS,
  resolveArtifactMaxChars,
  type ArtifactData,
  type ArtifactEmitterRef,
  type ArtifactKind,
  type EmitArtifact,
} from './types';
import { logger } from '../logger';

export const CREATE_ARTIFACT_TOOL_ID = 'create_artifact' as const;

const CREATE_ARTIFACT_DESCRIPTION =
  'Create an artifact: a substantial standalone deliverable (document, report, code file, HTML snippet, or data table) ' +
  'rendered for the user in a dedicated viewer card instead of chat text. ' +
  'Pass the FULL content — it is delivered to the user directly and is NOT echoed back to you (you receive a small ack). ' +
  'After calling, give a one-line summary in your reply; do not repeat the content. ' +
  "kinds: 'markdown' (documents/reports), 'code' (a source file; set language), 'html' (self-contained HTML, rendered sandboxed with no network access), " +
  "'table' (content must be a JSON array of uniform objects), " +
  "'chart' (content must be a JSON chart spec: {type:'bar'|'line', categories:string[], series:[{name,values:number[]}]} " +
  "or {type:'donut', segments:[{label,value}]}; all support optional title, caption, and valueFormat 'plain'|'usd'|'pct'). " +
  "Prefer 'chart' over describing numbers in prose whenever you are comparing quantities across categories or over time. " +
  'Use normal chat text for short answers — artifacts are for substantial deliverables.';

/** Zod input schema (AI SDK `inputSchema`; also the registry handler's validator). */
export const createArtifactInputSchema = z.object({
  title: z
    .string()
    .min(1)
    .max(200)
    .describe('Short human-readable title for the artifact (shown on its card).'),
  kind: z
    // NOT every ArtifactKind: `image` artifacts are emitted only by the image
    // tools, which point at bytes they stored themselves. See
    // MODEL_CREATABLE_ARTIFACT_KINDS for why a model-supplied image URL is not
    // something we render.
    .enum(MODEL_CREATABLE_ARTIFACT_KINDS)
    .describe(
      "What the content is: 'markdown' (document/report), 'code' (source file), 'html' (self-contained HTML snippet), 'table' (JSON array of uniform objects), or 'chart' (JSON chart spec).",
    ),
  language: z
    .string()
    .max(40)
    .optional()
    .describe("Programming language for kind 'code' (e.g. 'typescript', 'python', 'sql'). Omit for other kinds."),
  content: z
    .string()
    .min(1)
    .describe(
      'The FULL artifact content: markdown text, raw source code, a complete self-contained HTML snippet (no external scripts), ' +
        'a JSON array of uniform objects for tables, or a JSON chart spec for charts. ' +
        'Chart example: {"type":"bar","title":"Revenue by quarter","categories":["Q1","Q2"],"series":[{"name":"2026","values":[42000,51000]}],"valueFormat":"usd"}',
    ),
});

export type CreateArtifactInput = z.infer<typeof createArtifactInputSchema>;

/** Small ack returned into model context — never contains the content. */
export interface CreateArtifactAck {
  artifactId: string;
  title: string;
  kind: ArtifactKind;
  chars: number;
}

/** Error-shaped result (recoverable; the model reads it and adjusts). */
export interface CreateArtifactFailure {
  error: {
    code: 'CONTENT_TOO_LARGE' | 'UNSUPPORTED_SURFACE' | 'INVALID_CHART_SPEC';
    message: string;
  };
}

export type CreateArtifactResult = CreateArtifactAck | CreateArtifactFailure;

export function isCreateArtifactFailure(result: CreateArtifactResult): result is CreateArtifactFailure {
  return 'error' in result;
}

/**
 * Unique artifact id. Web Crypto's randomUUID exists on every runtime we ship
 * to (Node 18+/browsers); the fallback covers jsdom-style test environments
 * that lack it — uniqueness is what matters here, not unguessability.
 */
function generateArtifactId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `art-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Validate `chart` artifact content against the shared chart spec.
 *
 * Returns a model-readable error message, or `null` when the spec is valid.
 * The message names the exact field paths so the model can fix the spec rather
 * than guess at what "invalid" meant.
 */
export function validateChartSpec(content: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    return "Chart content must be valid JSON. Example: {\"type\":\"bar\",\"categories\":[\"Q1\",\"Q2\"],\"series\":[{\"name\":\"2026\",\"values\":[1,2]}]}";
  }

  const result = ChartSpecSchema.safeParse(parsed);
  if (result.success) return null;

  const issues = result.error.issues
    .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
    .join('; ');
  return `Chart spec is invalid — ${issues}. Supported: {type:'bar'|'line', categories, series:[{name, values}]} or {type:'donut', segments:[{label, value}]}.`;
}

/**
 * The single artifact executor. Emits the full content through `emitArtifact`
 * and returns only the small ack (or a recoverable error).
 *
 * Note on ids: `crypto.randomUUID()` instead of the package's `uuid`-based
 * chat-utils — `uuid` is pure ESM and statically importing it here would drag
 * it into every jest suite that imports the tool graph (same reason
 * bedrock-client lazy-imports its uuid-touching query module).
 */
export async function executeCreateArtifact(
  input: CreateArtifactInput,
  options: { emitArtifact?: EmitArtifact; maxChars?: number },
): Promise<CreateArtifactResult> {
  const maxChars = options.maxChars ?? resolveArtifactMaxChars();
  const chars = input.content.length;

  if (chars > maxChars) {
    // Expected, model-recoverable: shorten or split the deliverable.
    return {
      error: {
        code: 'CONTENT_TOO_LARGE',
        message: `Artifact content is ${chars} characters — the maximum is ${maxChars}. Shorten the content or split it into multiple smaller artifacts.`,
      },
    };
  }

  if (input.kind === 'chart') {
    // Validate the spec HERE, not in the viewer. The renderer is downstream of
    // the user: a malformed spec that reaches it produces a broken card the
    // model never learns about. Failing at create time returns the parse errors
    // into model context, where they are recoverable on the next step.
    const specError = validateChartSpec(input.content);
    if (specError) {
      return { error: { code: 'INVALID_CHART_SPEC', message: specError } };
    }
  }

  if (!options.emitArtifact) {
    // Documented graceful degrade: no stream writer on this surface. The model
    // already has the content (it was the tool input) — tell it to answer
    // inline rather than duplicating the content back through the result.
    return {
      error: {
        code: 'UNSUPPORTED_SURFACE',
        message:
          'Artifacts are not supported on this surface. Present the content directly in your reply instead of using create_artifact.',
      },
    };
  }

  const artifact: ArtifactData = {
    id: generateArtifactId(),
    title: input.title,
    kind: input.kind,
    ...(input.language ? { language: input.language } : {}),
    content: input.content,
    version: 1,
  };

  options.emitArtifact(artifact);

  logger.info('artifact emitted', {
    op: 'executeCreateArtifact',
    artifactId: artifact.id,
    kind: artifact.kind,
    chars,
  });

  return {
    artifactId: artifact.id,
    title: artifact.title,
    kind: artifact.kind,
    chars,
  };
}

/**
 * Build the AI SDK tool. The emitter is late-bound through `emitterRef`
 * because tool maps are constructed before the stream writer exists — the
 * streaming handler fills `emitterRef.current` inside `execute` scope.
 *
 * No `needsApproval`: pure content emission, no side effects (see module doc).
 */
export function buildCreateArtifactTool(options: { emitterRef?: ArtifactEmitterRef } = {}) {
  return tool({
    description: CREATE_ARTIFACT_DESCRIPTION,
    inputSchema: createArtifactInputSchema,
    execute: async (input: CreateArtifactInput): Promise<CreateArtifactResult> =>
      executeCreateArtifact(input, { emitArtifact: options.emitterRef?.current }),
  });
}

/** JSON Schema mirror of `createArtifactInputSchema` for the package tool registry (ajv-validated). */
const createArtifactJsonSchema: JSONSchema7 = {
  type: 'object',
  properties: {
    title: { type: 'string', minLength: 1, maxLength: 200 },
    // Mirror the zod enum, not ARTIFACT_KINDS: `image` is not model-creatable.
    kind: { type: 'string', enum: [...MODEL_CREATABLE_ARTIFACT_KINDS] },
    language: { type: 'string', maxLength: 40 },
    content: { type: 'string', minLength: 1 },
  },
  required: ['title', 'kind', 'content'],
};

/**
 * Package tool-registry definition so NON-hub consumers (which populate the
 * registry via `initializeToolRegistry` and build tools through
 * `buildToolsForAI`) also get `create_artifact`. The handler reads the emitter
 * from `ToolExecutionContext.emitArtifact` (wired by the streaming handler)
 * and delegates to the same `executeCreateArtifact`.
 *
 * NOT in the tool-search hot set on purpose — it defer-loads like any cold
 * tool, keeping the cached tool prefix stable.
 */
export const createArtifactToolDefinition: MeridianRegistryTool = {
  id: CREATE_ARTIFACT_TOOL_ID,
  name: 'Create Artifact',
  description: CREATE_ARTIFACT_DESCRIPTION,
  schema: createArtifactJsonSchema,
  requiresConfirmation: false,
  category: 'query',
  handler: {
    id: CREATE_ARTIFACT_TOOL_ID,
    execute: async (
      params: Record<string, unknown>,
      context?: ToolExecutionContext,
    ): Promise<ToolResult> => {
      const parsed = createArtifactInputSchema.safeParse(params);
      if (!parsed.success) {
        return {
          type: 'error',
          error: {
            code: 'INVALID_INPUT',
            message: `Invalid create_artifact input: ${parsed.error.issues.map((i) => i.message).join('; ')}`,
          },
        };
      }

      const result = await executeCreateArtifact(parsed.data, {
        emitArtifact: context?.emitArtifact,
      });

      if (isCreateArtifactFailure(result)) {
        return { type: 'error', error: result.error };
      }
      return { type: 'success', data: result };
    },
  },
};
